import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import { submitSellerApplication } from '../../util/api';
import { createResourceLocatorString } from '../../util/routes';
import { isTooManyEmailVerificationRequestsError } from '../../util/errors';
import {
  initialValuesForUserFields,
  isApprovedSeller,
  isSellerApplicationPending,
  isUserAuthorized,
  pickUserFieldsData,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { createSellerSearchTokens } from '../../util/sellerSearchTokens';
import { findCountry } from '../../util/shipping';
import { fetchCurrentUser, sendVerificationEmail } from '../../ducks/user.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import { H3, Page, UserNav, NamedLink, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import EmailVerificationInfo from '../AuthenticationPage/EmailVerificationInfo';

import ProfileSettingsForm from './ProfileSettingsForm/ProfileSettingsForm';
import { saveSellerApplyReturnPath } from './sellerApplyReturnPath';

import { clearUpdatedForm, updateProfile, uploadImage } from './ProfileSettingsPage.duck';
import css from './ProfileSettingsPage.module.css';

const SELLER_APPLICATION_PUBLIC_FIELD_KEYS = ['seller_category'];
const SELLER_APPLICATION_SOCIAL_KEYS = ['instagram', 'x', 'tiktok'];
const SELLER_FIELDS_USER_TYPE = 'seller';

const getUserFieldEnumLabel = (userFields, key, value) => {
  if (!value) {
    return null;
  }
  const field = userFields.find(f => f?.key === key);
  const option = field?.enumOptions?.find(o => o?.option === value);
  return option?.label || value;
};

const onImageUploadHandler = (values, fn) => {
  const { id, imageId, file } = values;
  if (file) {
    fn({ id, imageId, file });
  }
};

const ResendVerificationErrorMessage = props => {
  const { sendVerificationEmailError } = props;
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return sendVerificationEmailError ? (
    <div className={css.emailVerificationError}>
      <FormattedMessage id={resendErrorTranslationId} />
    </div>
  ) : null;
};

export const ViewProfileLink = props => {
  const { userUUID, isUnauthorizedUser, className, id } = props;
  console.log(id);
  return userUUID && isUnauthorizedUser ? (
    <NamedLink
      className={className ? className : css.profileLink}
      name="ProfilePageVariant"
      params={{ id: userUUID, variant: PROFILE_PAGE_PENDING_APPROVAL_VARIANT }}
    >
      {id == 'TopbarDesktop.profilePageLink' ? null : (
        <svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.99913 0C7.19358 0 4.94705 1.25781 3.31163 2.75488C1.68663 4.23828 0.599826 6.01562 0.0859375 7.23584C-0.0286458 7.50586 -0.0286458 7.80664 0.0859375 8.07666C0.599826 9.29688 1.68663 11.0742 3.31163 12.5576C4.94705 14.0547 7.19358 15.3125 9.99913 15.3125C12.8047 15.3125 15.0512 14.0547 16.6866 12.5576C18.3116 11.0708 19.3984 9.29688 19.9158 8.07666C20.0304 7.80664 20.0304 7.50586 19.9158 7.23584C19.3984 6.01562 18.3116 4.23828 16.6866 2.75488C15.0512 1.25781 12.8047 0 9.99913 0ZM4.99913 7.65625C4.99913 6.35089 5.52592 5.09899 6.4636 4.17596C7.40128 3.25293 8.67305 2.73438 9.99913 2.73438C11.3252 2.73438 12.597 3.25293 13.5347 4.17596C14.4723 5.09899 14.9991 6.35089 14.9991 7.65625C14.9991 8.96161 14.4723 10.2135 13.5347 11.1365C12.597 12.0596 11.3252 12.5781 9.99913 12.5781C8.67305 12.5781 7.40128 12.0596 6.4636 11.1365C5.52592 10.2135 4.99913 8.96161 4.99913 7.65625ZM9.99913 5.46875C9.99913 6.67529 9.0026 7.65625 7.77691 7.65625C7.53038 7.65625 7.29427 7.61523 7.07205 7.54346C6.88108 7.48193 6.65885 7.59814 6.6658 7.79639C6.67622 8.03223 6.71094 8.26807 6.77691 8.50391C7.2526 10.2539 9.08247 11.293 10.8602 10.8247C12.638 10.3564 13.6936 8.55518 13.2179 6.80518C12.8325 5.38672 11.5582 4.43311 10.1415 4.375C9.94011 4.36816 9.82205 4.5835 9.88455 4.7749C9.95747 4.99365 9.99913 5.22607 9.99913 5.46875Z"
            fill="#52525B"
          />
        </svg>
      )}

      <FormattedMessage id={id || 'ProfileSettingsPage.viewProfileLink'} />
    </NamedLink>
  ) : userUUID ? (
    <NamedLink
      className={className ? className : css.profileLink}
      name="ProfilePage"
      params={{ id: userUUID }}
    >
      {id == 'TopbarDesktop.profilePageLink' ? null : (
        <svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.99913 0C7.19358 0 4.94705 1.25781 3.31163 2.75488C1.68663 4.23828 0.599826 6.01562 0.0859375 7.23584C-0.0286458 7.50586 -0.0286458 7.80664 0.0859375 8.07666C0.599826 9.29688 1.68663 11.0742 3.31163 12.5576C4.94705 14.0547 7.19358 15.3125 9.99913 15.3125C12.8047 15.3125 15.0512 14.0547 16.6866 12.5576C18.3116 11.0708 19.3984 9.29688 19.9158 8.07666C20.0304 7.80664 20.0304 7.50586 19.9158 7.23584C19.3984 6.01562 18.3116 4.23828 16.6866 2.75488C15.0512 1.25781 12.8047 0 9.99913 0ZM4.99913 7.65625C4.99913 6.35089 5.52592 5.09899 6.4636 4.17596C7.40128 3.25293 8.67305 2.73438 9.99913 2.73438C11.3252 2.73438 12.597 3.25293 13.5347 4.17596C14.4723 5.09899 14.9991 6.35089 14.9991 7.65625C14.9991 8.96161 14.4723 10.2135 13.5347 11.1365C12.597 12.0596 11.3252 12.5781 9.99913 12.5781C8.67305 12.5781 7.40128 12.0596 6.4636 11.1365C5.52592 10.2135 4.99913 8.96161 4.99913 7.65625ZM9.99913 5.46875C9.99913 6.67529 9.0026 7.65625 7.77691 7.65625C7.53038 7.65625 7.29427 7.61523 7.07205 7.54346C6.88108 7.48193 6.65885 7.59814 6.6658 7.79639C6.67622 8.03223 6.71094 8.26807 6.77691 8.50391C7.2526 10.2539 9.08247 11.293 10.8602 10.8247C12.638 10.3564 13.6936 8.55518 13.2179 6.80518C12.8325 5.38672 11.5582 4.43311 10.1415 4.375C9.94011 4.36816 9.82205 4.5835 9.88455 4.7749C9.95747 4.99365 9.99913 5.22607 9.99913 5.46875Z"
            fill="#52525B"
          />
        </svg>
      )}

      <FormattedMessage id={id || 'ProfileSettingsPage.viewProfileLink'} />
    </NamedLink>
  ) : null;
};

/**
 * ProfileSettingsPage
 *
 * @component
 * @param {Object} props
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.image - The image
 * @param {string} props.image.id - The image id
 * @param {propTypes.uuid} props.image.imageId - The image id
 * @param {File} props.image.file - The image file
 * @param {propTypes.image} props.image.uploadedImage - The uploaded image
 * @param {Function} props.onImageUpload - The image upload function
 * @param {Function} props.onUpdateProfile - The update profile function
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {propTypes.error} props.updateProfileError - The update profile error
 * @param {propTypes.error} props.uploadImageError - The upload image error
 * @param {boolean} props.uploadInProgress - Whether the upload is in progress
 * @returns {JSX.Element}
 */
export const ProfileSettingsPageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const {
    currentUser,
    image,
    onImageUpload,
    onManageDisableScrolling,
    onUpdateProfile,
    onFetchCurrentUser,
    onResendVerificationEmail,
    location,
    scrollingDisabled,
    onChange,
    profileUpdated,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    sendVerificationEmailError,
    sendVerificationEmailInProgress,
  } = props;

  const [formNeedsEmailVerification, setFormNeedsEmailVerification] = useState(false);

  const { userFields, userTypes = [] } = config.user;
  const publicUserFields = userFields.filter(uf => uf.scope === 'public');
  const isSeller = isApprovedSeller(currentUser);
  const isApplicationPending = isSellerApplicationPending(currentUser);
  const emailVerified = !!currentUser?.attributes?.emailVerified;
  const applySeller = new URLSearchParams(location?.search || '').get('applySeller') === 'true';
  const isEmailVerificationRequiredForApply =
    !!currentUser?.id &&
    !emailVerified &&
    !isSeller &&
    !isApplicationPending &&
    (applySeller || formNeedsEmailVerification);
  const showPageLevelEmailVerification =
    !!currentUser?.id && !emailVerified && !isSeller && !isApplicationPending && applySeller;

  useEffect(() => {
    if (!isEmailVerificationRequiredForApply) {
      return;
    }

    const applyPath = createResourceLocatorString(
      'ProfileSettingsPage',
      routeConfiguration,
      {},
      { applySeller: true }
    );
    saveSellerApplyReturnPath(applyPath);

    const intervalId = window.setInterval(() => {
      onFetchCurrentUser({ enforce: true });
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [isEmailVerificationRequiredForApply, onFetchCurrentUser, routeConfiguration]);

  const handleSubmit = (values, userType) => {
    const {
      firstName,
      lastName,
      displayName,
      bio: rawBio,
      sellerApplicationIntent,
      ...rest
    } = values;
    const wantsToBecomeSeller = sellerApplicationIntent === 'yes';
    const shouldSaveSellerFields = wantsToBecomeSeller || isSeller || isApplicationPending;
    // Seller fields use seller config for picking. userType itself stays buyer until
    // Console approval, then the welcome poller promotes it to seller.
    const fieldUserType = shouldSaveSellerFields ? SELLER_FIELDS_USER_TYPE : userType;

    const displayNameMaybe = displayName
      ? { displayName: displayName.trim() }
      : { displayName: null };

    // Ensure that the optional bio is a string
    const bio = rawBio || '';
    const userFieldsForSave = userFields.filter(field => field?.key !== 'onlyfans');
    const pickedPublicData = pickUserFieldsData(rest, 'public', fieldUserType, userFieldsForSave);
    const sellerApplicationPublicData = shouldSaveSellerFields
      ? SELLER_APPLICATION_PUBLIC_FIELD_KEYS.reduce((fields, key) => {
          const value = rest[`pub_${key}`];
          return value ? { ...fields, [key]: value } : fields;
        }, {})
      : {};
    const sellerCountry = rest.pub_seller_country || null;
    const sellerCountryProtectedDataMaybe =
      shouldSaveSellerFields && sellerCountry
        ? {
            protectedData: {
              sellerCountry,
            },
          }
        : {};
    const publicData = {
      ...pickedPublicData,
      ...sellerApplicationPublicData,
      ...(isSeller ? { userType: SELLER_FIELDS_USER_TYPE } : {}),
    };
    const shouldUpdateSellerSearchTokens = isSeller || isApplicationPending || wantsToBecomeSeller;
    const sellerSearchTokensMaybe = shouldUpdateSellerSearchTokens
      ? {
          seller_search_tokens: createSellerSearchTokens({
            firstName,
            lastName,
            displayName: displayNameMaybe.displayName,
            publicData: {
              ...publicData,
              userType: SELLER_FIELDS_USER_TYPE,
            },
            userFields,
          }),
        }
      : {};

    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...displayNameMaybe,
      bio,
      ...sellerCountryProtectedDataMaybe,
      publicData: {
        ...publicData,
        ...sellerSearchTokensMaybe,
      },
    };
    const uploadedImage = props.image;

    // Update profileImage only if file system has been accessed
    const updatedValues =
      uploadedImage && uploadedImage.imageId && uploadedImage.file
        ? { ...profile, profileImageId: uploadedImage.imageId }
        : profile;

    const socialLinks = SELLER_APPLICATION_SOCIAL_KEYS.reduce((links, key) => {
      const value = rest[`pub_${key}`];
      if (typeof value !== 'string' || !value.trim()) {
        return links;
      }
      return { ...links, [key]: value.trim() };
    }, {});
    const sellerCategoryLabel = getUserFieldEnumLabel(
      userFields,
      'seller_category',
      rest.pub_seller_category
    );
    const sellerCountryLabel = findCountry(sellerCountry, intl.locale)?.name || sellerCountry;

    const saveProfile = () => onUpdateProfile(updatedValues);

    // New applications: set metadata.sellerApproved = false + email, then save fields.
    if (wantsToBecomeSeller && !isSeller && !isApplicationPending) {
      if (!emailVerified) {
        return Promise.resolve();
      }
      return submitSellerApplication({
        sellerCategory: sellerCategoryLabel,
        sellerCountry: sellerCountryLabel,
        sellerCountryCode: sellerCountry,
        socialLinks,
      })
        .then(() => saveProfile())
        .then(() => onFetchCurrentUser({ enforce: true }));
    }

    return saveProfile();
  };

  const user = ensureCurrentUser(currentUser);
  const {
    firstName,
    lastName,
    displayName,
    bio,
    publicData,
    protectedData,
  } = user?.attributes.profile;
  // I.e. the status is active, not pending-approval or banned
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);

  const { userType } = publicData || {};
  const sellerUserTypeConfig = userTypes.find(config => config.userType === 'seller');
  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };
  const userTypeConfig = userTypes.find(config => config.userType === userType);
  const isDisplayNameIncluded = userTypeConfig?.defaultUserFields?.displayName !== false;
  // ProfileSettingsForm decides if it's allowed to show the input field.
  const displayNameMaybe = isDisplayNameIncluded && displayName ? { displayName } : {};
  const sellerFieldsUserType =
    isSeller || isApplicationPending ? SELLER_FIELDS_USER_TYPE : userType;

  const profileSettingsForm = user.id ? (
    <ProfileSettingsForm
      className={css.form}
      currentUser={currentUser}
      initialValues={{
        firstName,
        lastName,
        ...displayNameMaybe,
        bio,
        profileImage: user.profileImage,
        ...initialValuesForUserFields(publicData, 'public', sellerFieldsUserType, userFields),
        pub_seller_category: publicData?.seller_category,
        pub_seller_country: protectedData?.sellerCountry || publicData?.seller_country,
      }}
      profileImage={profileImage}
      onImageUpload={e => onImageUploadHandler(e, onImageUpload)}
      uploadInProgress={uploadInProgress}
      updateInProgress={updateInProgress}
      uploadImageError={uploadImageError}
      updateProfileError={updateProfileError}
      ready={profileUpdated}
      onChange={onChange}
      onSubmit={values => handleSubmit(values, userType)}
      marketplaceName={config.marketplaceName}
      userFields={publicUserFields}
      userTypeConfig={userTypeConfig}
      sellerUserTypeConfig={sellerUserTypeConfig}
      isSeller={isSeller}
      isSellerApplicationPending={isApplicationPending}
      emailVerified={emailVerified}
      openSellerApplicationModal={
        applySeller && !isSeller && !isApplicationPending && emailVerified
      }
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailError={sendVerificationEmailError}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      onSellerApplicationEmailVerificationNeeded={setFormNeedsEmailVerification}
    />
  ) : null;

  const title = intl.formatMessage({ id: 'ProfileSettingsPage.title' });

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);
  const userFirstName = user?.attributes?.profile?.firstName;
  const userEmail = user?.attributes?.email;

  return (
    <Page className={css.root} title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
            <UserNav
              currentPage="ProfileSettingsPage"
              showManageListingsLink={showManageListingsLink}
            />
          </>
        }
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          {!isApplicationPending && !showPageLevelEmailVerification ? (
            <>
              <div className={css.headingContainer}>
                <H3 as="h1" className={css.heading}>
                  <FormattedMessage id="ProfileSettingsPage.heading" />
                </H3>
                <ViewProfileLink
                  userUUID={user?.id?.uuid}
                  isUnauthorizedUser={isUnauthorizedUser}
                />
              </div>
              <FormattedMessage id="ProfileSettingsPage.subHeading" />
            </>
          ) : null}
          {showPageLevelEmailVerification ? (
            <div className={css.emailVerificationStep}>
              <EmailVerificationInfo
                name={userFirstName}
                email={userEmail}
                showCloseLink={false}
                onResendVerificationEmail={onResendVerificationEmail}
                resendErrorMessage={
                  <ResendVerificationErrorMessage
                    sendVerificationEmailError={sendVerificationEmailError}
                  />
                }
                sendVerificationEmailInProgress={sendVerificationEmailInProgress}
              />
            </div>
          ) : (
            profileSettingsForm
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser, sendVerificationEmailError, sendVerificationEmailInProgress } = state.user;
  const {
    image,
    uploadImageError,
    uploadInProgress,
    profileUpdated,
    updateInProgress,
    updateProfileError,
  } = state.ProfileSettingsPage;
  return {
    currentUser,
    image,
    scrollingDisabled: isScrollingDisabled(state),
    profileUpdated,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    sendVerificationEmailError,
    sendVerificationEmailInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: () => dispatch(clearUpdatedForm()),
  onImageUpload: data => dispatch(uploadImage(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onFetchCurrentUser: options => dispatch(fetchCurrentUser(options)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

const ProfileSettingsPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  ProfileSettingsPageComponent
);

export default ProfileSettingsPage;
